import React, { useEffect, useRef, useState } from 'react';

/**
 * Dropdown menu for a workspace feed item — opens off the "..." button.
 *
 * Suno's equivalent ships:
 *   Remix · Edit · Publish · Share · Download · Manage · Add to Queue ·
 *   Add to Playlist · Song Radio · Move to Trash
 *
 * Our spec drops the music-app-only items (Add to Queue / Add to
 * Playlist / Song Radio) and renames Remix → Spin-off to match Epio
 * terminology. That leaves:
 *
 *   Spin-off · Edit · Publish · Share · Download · Manage · Move to Trash
 *
 * Each row calls a parent-provided handler. Parent decides whether
 * the action is implemented yet — unimplemented ones can pass null
 * and we hide that row, OR they pass a stub handler that toasts
 * "Coming soon."
 *
 * Closes on: outside click, Escape, or any item click.
 */
export default function WorkspaceItemMenu({
  open,
  onClose,
  onSpinOff,
  onRename,
  onEdit,
  onPublish,
  onShare,
  onDownload,
  onManage,
  onTrash,
}) {
  const ref = useRef(null);

  // Outside-click + Escape to close. Mounted only when open.
  useEffect(() => {
    if (!open) return;
    function onDoc(e) {
      if (ref.current && !ref.current.contains(e.target)) onClose?.();
    }
    function onKey(e) {
      if (e.key === 'Escape') onClose?.();
    }
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open, onClose]);

  if (!open) return null;

  const click = (fn) => () => {
    onClose?.();
    fn?.();
  };

  return (
    <div className="ws-item-menu" ref={ref} role="menu">
      {onSpinOff && (
        <button className="ws-item-menu-row" role="menuitem" onClick={click(onSpinOff)}>
          <Icon name="spin-off" />
          <span>Spin-off</span>
        </button>
      )}
      {onRename && (
        <button className="ws-item-menu-row" role="menuitem" onClick={click(onRename)}>
          <Icon name="rename" />
          <span>Edit title</span>
        </button>
      )}
      {onEdit && (
        <button className="ws-item-menu-row" role="menuitem" onClick={click(onEdit)}>
          <Icon name="edit" />
          <span>Edit</span>
        </button>
      )}
      {onPublish && (
        <button className="ws-item-menu-row" role="menuitem" onClick={click(onPublish)}>
          <Icon name="publish" />
          <span>Publish</span>
        </button>
      )}
      {onShare && (
        <button className="ws-item-menu-row" role="menuitem" onClick={click(onShare)}>
          <Icon name="share" />
          <span>Share</span>
        </button>
      )}
      {onDownload && (
        <button className="ws-item-menu-row" role="menuitem" onClick={click(onDownload)}>
          <Icon name="download" />
          <span>Download</span>
        </button>
      )}
      {onManage && (
        <button className="ws-item-menu-row" role="menuitem" onClick={click(onManage)}>
          <Icon name="manage" />
          <span>Manage</span>
        </button>
      )}
      {onTrash && (
        <>
          <div className="ws-item-menu-sep" aria-hidden />
          <button className="ws-item-menu-row ws-item-menu-row--danger" role="menuitem" onClick={click(onTrash)}>
            <Icon name="trash" />
            <span>Move to Trash</span>
          </button>
        </>
      )}
    </div>
  );
}

function Icon({ name }) {
  const common = { width: 14, height: 14, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 2, strokeLinecap: 'round', strokeLinejoin: 'round', 'aria-hidden': true };
  switch (name) {
    case 'spin-off':
      // Diagonal arrow icon — same shape used for "Use inspo" remix
      // button. Implies branching off from the source.
      return (
        <svg {...common}>
          <polyline points="16 3 21 3 21 8"/><line x1="4" y1="20" x2="21" y2="3"/>
          <polyline points="21 16 21 21 16 21"/><line x1="15" y1="15" x2="21" y2="21"/>
          <line x1="4" y1="4" x2="9" y2="9"/>
        </svg>
      );
    case 'rename':
      // Text/"type" glyph — distinguishes a quick title rename from the
      // full Edit (pencil) that opens the editor.
      return (
        <svg {...common}>
          <polyline points="4 7 4 4 20 4 20 7"/>
          <line x1="9" y1="20" x2="15" y2="20"/>
          <line x1="12" y1="4" x2="12" y2="20"/>
        </svg>
      );
    case 'edit':
      return (
        <svg {...common}>
          <path d="M12 20h9"/>
          <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4 12.5-12.5z"/>
        </svg>
      );
    case 'publish':
      return (
        <svg {...common}>
          <circle cx="12" cy="12" r="10"/>
          <line x1="2" y1="12" x2="22" y2="12"/>
          <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>
        </svg>
      );
    case 'share':
      return (
        <svg {...common}>
          <circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/>
          <line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/>
          <line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/>
        </svg>
      );
    case 'download':
      return (
        <svg {...common}>
          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
          <polyline points="7 10 12 15 17 10"/>
          <line x1="12" y1="15" x2="12" y2="3"/>
        </svg>
      );
    case 'manage':
      return (
        <svg {...common}>
          <path d="M4 6h16M4 12h16M4 18h16"/>
        </svg>
      );
    case 'trash':
      return (
        <svg {...common}>
          <polyline points="3 6 5 6 21 6"/>
          <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/>
          <path d="M10 11v6M14 11v6"/>
          <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/>
        </svg>
      );
    default:
      return null;
  }
}
